import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import EditBillForm from "./EditBillForm";
import defaultLogo from "../../../../assets/img/auth/logo.jpg"; // ✅ fallback logo
import { API_BASE_URL } from "../../../../config";

const BillView = ({ billId, onClose, adminPermissions, isSuperAdmin }) => {
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState({});
  const [project, setProject] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [generatedByAdmin, setGeneratedByAdmin] = useState(null);
  const [logoUrl, setLogoUrl] = useState(defaultLogo);
  const companyName = "Swaramayi Real Estate Marketing";

  // ✅ Format numbers
  const formatAmount = (num) => {
    if (num === null || num === undefined || num === "") return "0.00";
    return Number(num).toFixed(2);
  };

  // ✅ Format date with exact time
  const formatDateTime = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const hasPermission = (module, action) => {
    return (
      isSuperAdmin ||
      (adminPermissions[module] && adminPermissions[module][action])
    );
  };

  const handleRestrictedAction = (module, action, callback) => {
    if (!hasPermission(module, action)) {
      toast.error(`You don't have permission to ${action} ${module}`);
      return;
    }
    callback();
  };

  // ✅ Fetch bill, customer, project
  useEffect(() => {
    const fetchBill = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(
          `${API_BASE_URL}/api/billRecord/get-bill/${billId}`,
          { headers: { token } }
        );

        if (res.data.success) {
          const fetchedBill = res.data.result.billRecord;
          setBill(fetchedBill);

          // Customer
          if (typeof fetchedBill.customerId === "string") {
            const custRes = await axios.get(
              `${API_BASE_URL}/api/customer/get-customer/${fetchedBill.customerId}`,
              { headers: { token } }
            );
            if (custRes.data.success)
              setCustomer(custRes.data.result.customer || {});
          } else {
            setCustomer(fetchedBill.customerId || {});
          }

          // Project
          if (typeof fetchedBill.projectId === "string") {
            const projRes = await axios.get(
              `${API_BASE_URL}/api/project/get-project/${fetchedBill.projectId}`,
              { headers: { token } }
            );
            if (projRes.data.success)
              setProject(projRes.data.result.project || {});
          } else {
            setProject(fetchedBill.projectId || {});
          }
          // ✅ Fetch Generated By (Admin/Subadmin)
          if (fetchedBill.generatedBy) {
            try {
              // console.log("generatedBy", fetchedBill.generatedBy._id);
              const genRes = await axios.get(
                `${API_BASE_URL}/api/admin/get-subadmin/${fetchedBill.generatedBy._id}`,
                { headers: { token } }
              );
              // console.log("first", genRes);

              if (genRes.data.success && genRes.data.result?.subadmin) {
                setGeneratedByAdmin(genRes.data.result.subadmin);
              }
            } catch (err) {
              // console.error("Error fetching generatedBy admin:", err);
            }
          }
        } else {
          toast.error(res.data.message || "Bill not found");
        }
      } catch (error) {
        toast.error("Failed to fetch bill");
      } finally {
        setLoading(false);
      }
    };

    if (billId) fetchBill();
  }, [billId]);

  // ✅ Fetch company logo
  useEffect(() => {
    const token = localStorage.getItem("token");
    axios
      .get(`${API_BASE_URL}/api/admin/get-companylogo`, {
        headers: { token },
      })
      .then((res) => {
        const photoPath = res.data?.result?.profilePhoto;
        if (photoPath) {
          let finalPath = photoPath.startsWith("http")
            ? photoPath
            : `${API_BASE_URL}${photoPath.startsWith("/") ? photoPath : `/${photoPath}`
            }`;
          setLogoUrl(finalPath);
        } else {
          setLogoUrl(defaultLogo);
        }
      })
      .catch(() => setLogoUrl(defaultLogo));
  }, []);



  const downloadPDF = () => {
    if (!bill) return;
    const doc = new jsPDF();

    const pageWidth = doc.internal.pageSize.width;
    const margin = 15;

    // ---------- HEADER ----------
    doc.setFillColor(30, 60, 120);
    doc.rect(0, 0, pageWidth, 35, "F");

    if (logoUrl) {
      try {
        doc.addImage(logoUrl, "JPEG", margin, 5, 25, 20);
      } catch (e) {
        console.log("Logo not loaded in header");
      }
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(255, 255, 255);
    doc.text(
      companyName || "Swaramayi Real Estates Marketing",
      margin + 35,
      15
    );

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text("Real Estate Solutions", margin + 35, 23);

    let y = 45;

    // ---------- COST SHEET INFO ----------
    doc.setFontSize(11);
    doc.setTextColor(50, 50, 50);
    doc.text(`Cost Sheet ID: ${bill._id}`, margin, y);
    doc.text(`Date: ${formatDateTime(bill.createdAt)}`, pageWidth / 2, y);
    y += 15;

    doc.text(
      `Valid Till: ${bill.amountValidTill ? formatDateTime(bill.amountValidTill) : "N/A"
      }`,
      margin,
      y
    );
    y += 15;

    // ---------- FROM / TO ----------
    autoTable(doc, {
      startY: y,
      head: [["Share By", "Share To"]],
      body: [
        [
          // ✅ Static company details
          `Swaramayi Real Estate Marketing
Madhyamgram, East Udayrajpur, Kolkata 70129
Email: srem@swaramayi.in
Website: www.swaramayi.in
Phone: 8902130791
RERA No - WBRERA/A/NOR/2025/000737`,

          // ✅ Customer details
          `${customer.customerName || "Customer Name"}\n${customer.customerAddress || "Location"
          }\n${customer.customerNumber || "Phone: N/A"}`,

          // `${project?.projectName}\n${
          //   project?.projectAddress
          // }\n${project?.projectContactNumber}\n${project?.projectContactName}`,
        ],
      ],
      theme: "grid",
      styles: { fontSize: 10, cellPadding: 3 },
      headStyles: { fillColor: [30, 60, 120], textColor: 255 },
    });

    y = doc.lastAutoTable.finalY + 10;

    // ---------- PROJECT DETAILS ----------
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(30, 60, 120);
    doc.text("Project Details", margin, y);
    y += 6;

    autoTable(doc, {
      startY: y,
      head: [
        ["Project", "Type", "Flat Type", "Property on Floor", "Amenities", "Address"],
      ],
      body: [
        [
          project.projectName || "-",
          project.projectType || "-",
          project.flatType || "-",
          project.propertyOnFloorNumber || "-",
          project.amenities || "-",
          project.projectAddress || "-",
        ],
      ],
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [200, 200, 200], textColor: 0 },
    });

    y = doc.lastAutoTable.finalY + 10;

    // ---------- COST SHEET PRICE BREAKDOWN ----------
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(30, 60, 120);
    doc.text("Cost Sheet Breakdown", margin, y);
    y += 6;

    const tableData = [
      [
        "Flat Price",
        `${formatAmount(bill.areaSqft)} X Rs.${formatAmount(bill.ratePerSqft)}`,
        `Rs. ${formatAmount(bill.basePrice)}`,
      ],
      ["Amenities Charge", project?.amenities, `Rs. ${formatAmount(bill.amenitiesCharge)}`],
      ["Parking", 'Parking Space', `Rs. ${formatAmount(bill.parking?.total)}`],
      ["Extra Charges", 'Maintenance, etc.', `Rs. ${formatAmount(bill.extraCharges)}`],
      ["Discount", 'Special Offer', `- Rs. ${formatAmount(bill.discount)}`],
      [
        "Tax %",
        `${bill.taxPercent}%`,
        `Rs. ${formatAmount((bill.totalAmount * bill.taxPercent) / 100)}`,
      ],
    ];

    autoTable(doc, {
      startY: y,
      head: [["Description", "Details", "Amount"]],
      body: tableData,
      theme: "striped",
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [30, 60, 120], textColor: 255 },
      columnStyles: {
        0: { cellWidth: 70 },
        1: { cellWidth: 60 },
        2: { cellWidth: 50, halign: "right" },
      },
    });

    y = doc.lastAutoTable.finalY + 5;

    // ---------- TOTAL AMOUNT ----------
    doc.setFillColor(0, 150, 100);
    doc.setTextColor(255, 255, 255);
    doc.rect(margin, y, pageWidth - 2 * margin, 12, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text("TOTAL AMOUNT", margin + 2, y + 8);
    doc.text(
      `Rs. ${bill.totalAmount.toLocaleString("en-IN")}`,
      pageWidth - margin - 5,
      y + 8,
      { align: "right" }
    );

    y += 20;

    // ---------- REMARKS ----------
    if (bill.remarks) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      doc.text("Remarks:", margin, y);
      y += 6;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      const remarksLines = doc.splitTextToSize(
        bill.remarks,
        pageWidth - 2 * margin
      );
      doc.text(remarksLines, margin, y);

      // move y below remarks text
      y += remarksLines.length * 5 + 10;
    } else {
      y += 10;
    }

    /// ---------- NOTE ----------
    doc.setFillColor(255, 255, 200); // halka yellow background
    doc.setDrawColor(200, 200, 0); // yellow border
    doc.rect(margin, y, pageWidth - 2 * margin, 15, "FD");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(120, 100, 0);
    doc.text(
      "Note: This cost sheet is valid for 7 days only.",
      margin + 5,
      y + 10
    );

    y += 25; // move below note box

    // // ---------- FOOTER ----------
    // doc.setFont("helvetica", "italic");
    // doc.setFontSize(8);
    // doc.setTextColor(120, 120, 120);

    // doc.text("This is a computer generated cost sheet.", margin, y + 10);

    // doc.text(
    //   `Generated on: ${new Date().toLocaleString()}`,
    //   pageWidth - margin - 45,
    //   y + 10
    // );

    doc.save(`CostSheet-${bill._id}.pdf`);
  };

  // ✅ Delete API
  const performDelete = async () => {
    if (!bill) return;
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API_BASE_URL}/api/billRecord/delete-bill/${bill._id}`,
        {},
        { headers: { token } }
      );

      if (res.data.success) {
        toast.success(res.data.message || "Bill deleted successfully");
        onClose();
      } else {
        toast.error(res.data.message || "Failed to delete bill");
      }
    } catch (error) {
      toast.error("Error deleting bill");
    } finally {
      setShowDeleteModal(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-white p-4">
      {loading ? (
        <p>Loading bill details...</p>
      ) : !bill ? (
        <p className="text-red-500">Bill not found</p>
      ) : (
        <div className="mx-auto max-w-5xl rounded-lg bg-white shadow-lg">
          {/* Header */}
          <div className="flex items-center justify-between border-b p-5">
            <div className="flex items-center gap-4">
              <img
                src={logoUrl}
                alt="Company Logo"
                onError={() => setLogoUrl(defaultLogo)}
                className="h-16 w-16 object-contain"
              />
              <h1 className="text-2xl font-bold">{companyName}</h1>
            </div>
            <div className="flex gap-2">
              <button
                onClick={downloadPDF}
                className="rounded bg-blue-600 px-3 py-2 text-white hover:bg-blue-700"
              >
                Download PDF
              </button>
              {/* <button
                onClick={() => setShowEditModal(true)}
                className="rounded bg-green-600 px-3 py-2 text-white hover:bg-green-700"
              >
                Edit
              </button>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="rounded bg-red-600 px-3 py-2 text-white hover:bg-red-700"
              >
                Delete
              </button> */}
              {/* ✅ Edit Button */}
              <button
                onClick={() =>
                  handleRestrictedAction("bill", "edit", () =>
                    setShowEditModal(true)
                  )
                }
                disabled={!hasPermission("bill", "edit")}
                className={`rounded px-3 py-2 text-white 
    ${hasPermission("bill", "edit")
                    ? "bg-green-600 hover:bg-green-700"
                    : "cursor-not-allowed bg-gray-400"
                  }
  `}
              >
                Edit
              </button>

              {/* ✅ Delete Button */}
              <button
                onClick={() =>
                  handleRestrictedAction("bill", "delete", () =>
                    setShowDeleteModal(true)
                  )
                }
                disabled={!hasPermission("bill", "delete")}
                className={`rounded px-3 py-2 text-white 
    ${hasPermission("bill", "delete")
                    ? "bg-red-600 hover:bg-red-700"
                    : "cursor-not-allowed bg-gray-400"
                  }
  `}
              >
                Delete
              </button>

              <button
                onClick={onClose}
                className="text-xl text-gray-500 hover:text-red-500"
              >
                ✖
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="space-y-6 p-6">
            {/* Customer & Project Info */}
            <div className="grid grid-cols-1 gap-6 rounded-lg border bg-gray-50 p-4 md:grid-cols-2">
              <div>
                <h3 className="mb-3 text-lg font-semibold">Customer Details</h3>
                <p>
                  <strong>Name:</strong> {customer.customerName || "-"}
                </p>
                <p>
                  <strong>Phone:</strong> {customer.customerNumber || "-"}
                </p>
                <p>
                  <strong>Location:</strong> {customer.locationRequired || "-"}
                </p>
              </div>
              <div>
                <h3 className="mb-3 text-lg font-semibold">Project Details</h3>
                <p>
                  <strong>Project:</strong> {project.projectName || "-"}
                </p>
                <p>
                  <strong>Type:</strong> {project.projectType || "-"}
                </p>
                <p>
                  <strong>Flat Type:</strong> {project.flatType || "-"}
                </p>

                <p>
                  <strong>Property on Floor:</strong> {project.propertyOnFloorNumber || "-"}
                </p>

                <p>
                  <strong>Amenities:</strong> {project.amenities || "-"}
                </p>
                <p>
                  <strong>Address:</strong> {project.projectAddress || "-"}
                </p>
              </div>
            </div>

            {/* Bill Info */}
            <div className="space-y-4 rounded-lg border p-4">
              <h3 className="mb-2 text-lg font-semibold">
                Cost Sheet Information
              </h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <p>
                  <strong>Bill ID:</strong> {bill._id}
                </p>
                <p>
                  <strong>Date:</strong> {formatDateTime(bill.createdAt)}
                </p>
                <p>
                  <strong>Generated By:</strong>{" "}
                  {generatedByAdmin ? generatedByAdmin.adminName : "N/A"}
                </p>
                <p>
                  <strong>Valid Till:</strong>{" "}
                  {bill.amountValidTill
                    ? formatDateTime(bill.amountValidTill)
                    : "-"}
                </p>
                <p>
                  <strong>Rate/Sqft:</strong> {formatAmount(bill.ratePerSqft)}
                </p>
                <p>
                  <strong>Area (Sqft):</strong> {formatAmount(bill.areaSqft)}
                </p>
                <p>
                  <strong>Base Price:</strong> {formatAmount(bill.basePrice)}
                </p>
                <p>
                  <strong>Amenities Charge:</strong>{" "}
                  {formatAmount(bill.amenitiesCharge)}
                </p>
                <p>
                  <strong>Parking:</strong> {formatAmount(bill.parking?.total)}
                </p>
                <p>
                  <strong>Extra Charges:</strong>{" "}
                  {formatAmount(bill.extraCharges)}
                </p>
                <p>
                  <strong>Discount:</strong> -{formatAmount(bill.discount)}
                </p>
                <p>
                  <strong>Tax %:</strong> {formatAmount(bill.taxPercent)} %
                </p>
                <p className="text-lg font-bold">
                  <strong>Total Amount:</strong>{" "}
                  {formatAmount(bill.totalAmount)}
                </p>
              </div>
              <div>
                <p>
                  <strong>Remarks:</strong> {bill.remarks || "-"}
                </p>
              </div>
            </div>

            {/* Note Section */}
            <div className="rounded border border-yellow-300 bg-yellow-50 p-4">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> This cost sheet is valid for{" "}
                <b>7 days</b> only.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="bg-black/50 fixed inset-0 z-50 flex items-center justify-center">
          <div className="w-[300px] rounded-lg bg-white p-6 shadow-lg">
            <h2 className="mb-4 text-lg font-semibold">
              Are you sure you want to Delete this bill?
            </h2>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="rounded border px-4 py-2 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={performDelete}
                className="rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <EditBillForm
          bill={bill}
          customer={customer}
          project={project}
          onClose={() => setShowEditModal(false)}
          onBillUpdated={(updatedBill) => setBill(updatedBill)}
        />
      )}
    </div>
  );
};

export default BillView;
